import { v4 as uuidv4 } from 'uuid';
import { useCallback, useMemo, useState } from 'react';
import { Accept, FileRejection } from 'react-dropzone';
import classNames from 'classnames';

import { formatBytes, getFileDropzoneRejectionMessage, uploadFile } from '@/lib/utils';
import { FileRestrictionsModel, PresignedUploadModel, DOCUMENT_UPLOAD_STATUS_ID } from '@/lib/models';
import { FileUploaderDropzone } from '@/components/file-uploader-dropzone';
import { CloudArrowUp, File, Trash } from 'react-bootstrap-icons';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { Meter } from './meter';

const useStyles = createUseThemedStyles((theme) => ({
	fileUploader: {
		display: 'flex',
		minHeight: '116px',
		padding: '20px 24px',
		borderRadius: '5px',
		alignItems: 'center',
		border: `1px solid ${theme.colors.gray300}`,
	},
	idle: {
		border: `1px dashed ${theme.colors.gray300}`,
	},
	iconOuter: {
		flexShrink: 0,
	},
	pendingIcon: {
		width: '64px',
		height: ' 64px',
		display: 'flex',
		borderRadius: '50%',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#e9ecef',
		'& > i, & > span': {
			fontSize: '24px',
		},
	},
	preview: {
		width: '150px',
		position: 'relative',
		paddingBottom: '56%',
		backgroundColor: '#e9ecef',
	},
	previewInner: {
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		position: 'absolute',
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		'& .file-icon': {
			fontSize: '32px',
		},
	},
	content: {
		flex: 1,
		paddingLeft: '20px',
	},
}));

enum FILE_UPLOADER_STATUS {
	IDLE = 'IDLE',
	PENDING = 'PENDING',
	SUCCESS = 'SUCCESS',
	ERROR = 'ERROR',
}

export type fileUploaderState =
	| { status: FILE_UPLOADER_STATUS.IDLE }
	| { status: FILE_UPLOADER_STATUS.PENDING; file: File }
	| { status: FILE_UPLOADER_STATUS.SUCCESS; file: File; result: PresignedUploadModel }
	| { status: FILE_UPLOADER_STATUS.ERROR; file: File; error?: unknown };

const IDLE_STATE: fileUploaderState = Object.freeze({ status: FILE_UPLOADER_STATUS.IDLE });

interface FileUploaderProps {
	accept: Accept;
	fileRestrictions: FileRestrictionsModel;
	presignedUploadGetter(fileInfo: {
		fileId: string;
		fileName: string;
		fileType: string;
		fileSize: number;
	}): Promise<{ upload: PresignedUploadModel }>;
	uploadStatusUpdater(upload: PresignedUploadModel, uploadStatusId: DOCUMENT_UPLOAD_STATUS_ID): Promise<void>;
	onChange(
		result: FileUploaderFileValue & {
			fileRemoved: boolean;
		}
	): void;
	showImageDimensions?: boolean;
	value?: FileUploaderFileValue;
}

export type FileUploaderFileValue = {
	fileId: string;
	fileName: string;
	fileSize: number;
	fileSizeDescription: string;
	fileType: string;
	fileUrl: string;
};

export const FileUploader = ({
	accept,
	fileRestrictions,
	presignedUploadGetter,
	uploadStatusUpdater,
	onChange,
	showImageDimensions,
	value,
}: FileUploaderProps) => {
	const handleError = useHandleError();
	const classes = useStyles();
	const [fileUploaderState, setFileUploaderState] = useState<fileUploaderState>(IDLE_STATE);
	const [uploadProgress, setUploadProgress] = useState(0);
	const previewType = useMemo(() => value?.fileType.split('/')[0], [value]);

	const handleFileDropzoneChange = useCallback(
		async ([file]: File[], fileRejections: FileRejection[]) => {
			if (fileRejections.length > 0) {
				window.alert(getFileDropzoneRejectionMessage(fileRejections));
				return;
			}

			const fileId = uuidv4();
			setFileUploaderState({ status: FILE_UPLOADER_STATUS.PENDING, file });

			try {
				const result = await uploadFile({
					file,
					presignedUploadGetter: () =>
						presignedUploadGetter({
							fileId,
							fileName: file.name,
							fileType: file.type,
							fileSize: file.size,
						}),
					uploadStatusUpdater,
					onProgress: setUploadProgress,
				});

				setFileUploaderState({ status: FILE_UPLOADER_STATUS.SUCCESS, file, result });
				setTimeout(() => {
					setFileUploaderState(IDLE_STATE);
					setUploadProgress(0);
					onChange({
						fileRemoved: false,
						fileId,
						fileName: file.name,
						fileSize: file.size,
						fileSizeDescription: formatBytes(file.size, { binary: false, decimals: 1 }),
						fileType: file.type,
						fileUrl: result.accessUrl,
					});
				}, 500);
			} catch (error) {
				handleError(error);
				setFileUploaderState({ status: FILE_UPLOADER_STATUS.ERROR, file, error });
				onChange({
					fileRemoved: true,
					fileId,
					fileName: '',
					fileSize: 0,
					fileSizeDescription: '',
					fileType: '',
					fileUrl: '',
				});
			}
		},
		[handleError, onChange, presignedUploadGetter, uploadStatusUpdater]
	);

	const handleDeleteButtonClick = () => {
		setFileUploaderState(IDLE_STATE);
		onChange({
			fileRemoved: true,
			fileId: '',
			fileName: '',
			fileSize: 0,
			fileSizeDescription: '',
			fileType: '',
			fileUrl: '',
		});
	};

	if (value) {
		return (
			<div className={classes.fileUploader}>
				<div className={classes.iconOuter}>
					<div className={classes.preview}>
						<div
							className={classes.previewInner}
							style={{ ...(previewType === 'image' && { backgroundImage: `url(${value.fileUrl})` }) }}
						>
							{previewType === 'video' && (
								<video width="100%" height="100%" controls>
									<source src={value.fileUrl} type={value.fileType}></source>
								</video>
							)}
							{previewType !== 'image' && previewType !== 'video' && <File />}
						</div>
					</div>
				</div>
				<div className={classes.content}>
					<div className="d-flex align-items-center justify-content-between">
						<p className="margin-0 font-weight-bold">{value.fileName}</p>
						<button type="button" className="btn btn-icon text-danger" onClick={handleDeleteButtonClick}>
							<Trash />
						</button>
					</div>
					<p className="margin-0 text-secondary">{value.fileSizeDescription}</p>
				</div>
			</div>
		);
	}

	return (
		<>
			{fileUploaderState.status === FILE_UPLOADER_STATUS.IDLE && (
				<FileUploaderDropzone
					accept={accept}
					maxSize={fileRestrictions.maxFileSize}
					maxFiles={1}
					onChange={handleFileDropzoneChange}
				>
					<div className={classNames(classes.fileUploader, classes.idle)}>
						<div className="flex-1 d-flex flex-column justify-content-center align-items-center">
							<p className="margin-0 text-center font-weight-bold">
								<span className="text-primary">Click to browse files</span> or drag &amp; drop
							</p>
							<p className="margin-0 text-center text-secondary">
								Supported files:{' '}
								{Object.values(accept)
									.flatMap((s) => s)
									.join(', ')}{' '}
								{showImageDimensions &&
									`(min. ${fileRestrictions.minImageWidth}x${fileRestrictions.minImageHeight}px)`}
							</p>
						</div>
					</div>
				</FileUploaderDropzone>
			)}

			{fileUploaderState.status !== FILE_UPLOADER_STATUS.IDLE && (
				<div className={classes.fileUploader}>
					<div className={classes.iconOuter}>
						<div className={classes.pendingIcon}>
							<CloudArrowUp />
						</div>
					</div>
					<div className="react-file-uploader__content">
						<div className="margin-bottom-15 d-flex align-items-center justify-content-between">
							<p className="margin-0 font-weight-bold">{fileUploaderState.file.name}</p>
							<button type="button" className="btn btn-icon text-secondary" disabled>
								<Trash />
							</button>
						</div>
						<Meter
							className="mb-3"
							variant={
								fileUploaderState.status === FILE_UPLOADER_STATUS.SUCCESS
									? 'success'
									: fileUploaderState.status === FILE_UPLOADER_STATUS.ERROR
									? 'danger'
									: 'primary'
							}
							value={uploadProgress}
							max={100}
						/>

						{fileUploaderState.status === FILE_UPLOADER_STATUS.PENDING && (
							<div className="d-flex align-items-center justify-content-between">
								<p className="margin-0 text-secondary">Uploading...</p>
								<p className="margin-0 text-secondary">{uploadProgress}%</p>
							</div>
						)}

						{fileUploaderState.status === FILE_UPLOADER_STATUS.SUCCESS && (
							<div className="d-flex align-items-center justify-content-between">
								<p className="margin-0 text-success">
									<i className="icon-check" /> Uploaded
								</p>
								<p className="margin-0 text-secondary">100%</p>
							</div>
						)}

						{fileUploaderState.status === FILE_UPLOADER_STATUS.ERROR && (
							<div className="d-flex align-items-center justify-content-between">
								<p className="margin-0 text-danger">
									<i className="icon-error" /> Upload failed. Please try again.
								</p>
								<button
									type="button"
									className="btn btn-link text-danger"
									onClick={handleDeleteButtonClick}
								>
									Try again
								</button>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};
